import { useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../context/AuthContext'
import { setAccessToken } from '../api/client'
import { FortisPayment, type FortisPaymentHandle } from '../components/payments/FortisPayment'
import api from '../api/client'
import type { FortisTransactionData, CostItem, User } from '../types'

type Step = 'form' | 'checkout' | 'processing' | 'pending' | 'success'

interface JoinForm {
  firstName: string
  lastName: string
  address: string
  city: string
  state: string
  zip: string
  phone: string
  email: string
  password: string
  confirmPassword: string
  membershipType: 'individual' | 'junior'
  // Junior enrollment is parent-completed: no email is collected for the
  // junior member; the guardian's email is the account contact.
  guardianName: string
  guardianEmail: string
  guardianConsent: boolean
}

interface JoinStartResponse {
  clientToken: string
  env: 'sandbox' | 'production'
  transactionId: string
  costData: { total: number; items: CostItem[] }
}

interface JoinCompleteResponse {
  success: boolean
  auid: string
  accessToken: string
  emailSent?: boolean
  loginName?: string | null
  user: User
  // The card processor gave no clear answer and the server could not tell whether it
  // charged the card: the join waits for the AU office, and the member must not pay again.
  pending?: boolean
  message?: string
}

// Online join is individual-only (Individual or Junior). Family membership is
// managed by an existing member from their profile, not the public signup form.
const MEMBERSHIP_OPTIONS = [
  { value: 'individual' as const, label: 'Individual', cost: 30, description: 'Standard adult membership' },
  { value: 'junior' as const, label: 'Junior', cost: 10, description: 'Ages 18 and under - completed by a parent or guardian' },
]

// New-member welcome message shown on the post-signup success screen.
// Copy provided by Lauren (2026-06-30); "simple for now" and expected to evolve.
const WELCOME_MESSAGE =
  'Your online account has been successfully created. You can now sign in to view ' +
  'your account, manage your information, and access our online services. Thank you ' +
  'for joining us!'

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC','PR','VI','GU','AS',
]

// Format a US phone number as the user types: 8476914611 -> 847-691-4611.
// The server normalizes again on submit; this keeps what the user sees and
// what the office receives identical.
function formatPhoneInput(value: string): string {
  let digits = value.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
  digits = digits.slice(0, 10)
  if (digits.length > 6) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return digits
}

export function Join() {
  const navigate = useNavigate()
  const { refreshUser } = useAuth()
  const fortisRef = useRef<FortisPaymentHandle>(null)
  // One payment completes the join once. A second success callback (a second
  // payment element, or the element reporting twice) is ignored while one
  // completion is in flight or done; the server refuses it too.
  const completedRef = useRef(false)

  const [step, setStep] = useState<Step>('form')
  const [error, setError] = useState('')
  const [pendingMessage, setPendingMessage] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof JoinForm, string>>>({})

  // Form data
  const [form, setForm] = useState<JoinForm>({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    membershipType: 'individual',
    guardianName: '',
    guardianEmail: '',
    guardianConsent: false,
  })

  // Payment session
  const [clientToken, setClientToken] = useState('')
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('production')
  const [transactionId, setTransactionId] = useState('')
  const [costItems, setCostItems] = useState<CostItem[]>([])
  const [total, setTotal] = useState(0)

  // Success state
  const [newAuid, setNewAuid] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [juniorLoginName, setJuniorLoginName] = useState('')

  function updateField(field: keyof JoinForm, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  function validate(): boolean {
    const errors: Partial<Record<keyof JoinForm, string>> = {}

    if (!form.firstName.trim()) errors.firstName = 'Required'
    if (!form.lastName.trim()) errors.lastName = 'Required'
    if (!form.address.trim()) errors.address = 'Required'
    if (!form.city.trim()) errors.city = 'Required'
    if (!form.state) errors.state = 'Required'
    if (!form.zip.trim()) errors.zip = 'Required'
    if (!form.phone.trim()) {
      errors.phone = 'Required'
    } else if (form.phone.replace(/\D/g, '').length !== 10) {
      errors.phone = 'Enter a 10-digit phone number'
    }

    if (form.membershipType === 'junior') {
      if (!form.guardianName.trim()) errors.guardianName = 'Required'
      if (!form.guardianEmail.trim()) {
        errors.guardianEmail = 'Required'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.guardianEmail)) {
        errors.guardianEmail = 'Invalid email'
      }
      if (!form.guardianConsent) errors.guardianConsent = 'Parent/guardian consent is required'
    } else if (!form.email.trim()) {
      errors.email = 'Required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = 'Invalid email'
    }

    if (!form.password) {
      errors.password = 'Required'
    } else if (form.password.length < 8) {
      errors.password = 'At least 8 characters'
    }

    if (form.password !== form.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match'
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  async function handleSubmitForm(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return

    setError('')
    setStep('processing')
    try {
      const { data } = await api.post<JoinStartResponse>('/join/start', {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state,
        zip: form.zip.trim(),
        phone: form.phone.trim(),
        password: form.password,
        membershipType: form.membershipType,
        ...(form.membershipType === 'junior'
          ? {
              guardianName: form.guardianName.trim(),
              guardianEmail: form.guardianEmail.trim(),
              guardianConsent: form.guardianConsent,
            }
          : { email: form.email.trim() }),
      })
      setClientToken(data.clientToken)
      setEnvironment(data.env)
      setTransactionId(data.transactionId)
      setCostItems(data.costData.items)
      setTotal(data.costData.total)
      setStep('checkout')
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Unable to start registration. Please try again.'
      setError(message)
      setStep('form')
    }
  }

  function handlePayNow() {
    setError('')
    setStep('processing')
    fortisRef.current?.submit()
  }

  async function handleFortisSuccess(fortisData: FortisTransactionData) {
    if (completedRef.current) return
    completedRef.current = true
    let data: JoinCompleteResponse
    try {
      ({ data } = await api.post<JoinCompleteResponse>('/join/complete', {
        transactionId,
        fortisTransaction: fortisData,
      }))
    } catch (err: unknown) {
      completedRef.current = false
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'There was an issue completing your registration. Please contact the AU office.'
      setError(message)
      setStep('checkout')
      return
    }
    if (data.pending) {
      // completedRef stays set and the payment form is gone: no second payment.
      setPendingMessage(data.message || 'Your payment is still being confirmed. Please do not pay again. The AU office will confirm your membership.')
      setStep('pending')
      return
    }
    setNewAuid(data.auid)
    setEmailSent(!!data.emailSent)
    setJuniorLoginName(data.loginName || '')

    // Auto-login: set the access token and refresh auth context. The membership is
    // already created and paid here, so a failed refresh must not send the member
    // back to checkout to pay again; they can still sign in with the new account.
    setAccessToken(data.accessToken)
    try {
      await refreshUser()
    } catch {
      // keep going to the success page
    }

    setStep('success')
  }

  function handleFortisError(msg: string) {
    setError(msg)
    setStep('checkout')
  }

  const formatMoney = (amount: number) =>
    '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  const selectedMembership = MEMBERSHIP_OPTIONS.find((m) => m.value === form.membershipType)!

  // -- Payment not yet confirmed --
  if (step === 'pending') {
    return (
      <>
        <Helmet>
          <title>Payment being confirmed - ARPU</title>
        </Helmet>
        <div className="max-w-lg mx-auto mt-8 text-center">
          <div className="bg-warning/10 border border-warning/30 rounded-lg p-8">
            <h1 className="text-2xl font-heading font-bold text-text mb-3">
              Payment being confirmed
            </h1>
            <p className="text-text-light">{pendingMessage}</p>
          </div>
        </div>
      </>
    )
  }

  // -- Success --
  if (step === 'success') {
    return (
      <>
        <Helmet>
          <title>Welcome to the AU - ARPU</title>
        </Helmet>
        <div className="max-w-lg mx-auto mt-8 text-center">
          <div className="bg-success/10 border border-success/30 rounded-lg p-8">
            <h1 className="text-2xl font-heading font-bold text-success mb-3">
              Welcome to the AU!
            </h1>
            <p className="text-text-light mb-4">{WELCOME_MESSAGE}</p>
            <p className="text-text-light mb-2">
              Your AU ID is <strong className="text-text">{newAuid}</strong>
            </p>
            {juniorLoginName && (
              <p className="text-text-light mb-2">
                This junior account signs in with the username{' '}
                <strong className="text-text">{juniorLoginName}</strong> instead of an
                email address. Please write it down - password resets go to the
                parent/guardian email on file.
              </p>
            )}
            <p className="text-text-muted text-sm mb-6">
              {emailSent
                ? `A confirmation email has been sent to ${form.membershipType === 'junior' ? form.guardianEmail : form.email}.`
                : 'Please save your AU ID. If you do not receive a confirmation email, contact the AU office.'}
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-block px-6 py-2.5 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </>
    )
  }

  // -- Checkout (payment step) --
  if (step === 'checkout' || (step === 'processing' && clientToken)) {
    return (
      <>
        <Helmet>
          <title>Complete Payment - Join the AU</title>
        </Helmet>
        <div className="max-w-2xl mx-auto mt-8">
          <h1 className="text-2xl font-heading font-bold text-text mb-6">Complete Payment</h1>

          {/* Order summary */}
          <div className="bg-surface-alt border border-border rounded-lg p-4 mb-6">
            <p className="text-sm text-text-muted mb-2">
              Joining as: <strong className="text-text">{form.firstName} {form.lastName}</strong>
            </p>
            {costItems.map((item, i) => (
              <div key={i} className="flex justify-between text-sm text-text-light py-1">
                <span>{item.label}</span>
                <span>{formatMoney(item.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between font-semibold text-text border-t border-border mt-2 pt-2">
              <span>Total</span>
              <span>{formatMoney(total)}</span>
            </div>
          </div>

          {error && (
            <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 text-sm mb-4">
              {error}
            </div>
          )}

          <div className="mb-6">
            <h2 className="text-lg font-heading font-semibold text-text mb-3">Payment</h2>
            <FortisPayment
              ref={fortisRef}
              clientToken={clientToken}
              environment={environment}
              onSuccess={handleFortisSuccess}
              onError={handleFortisError}
            />
          </div>

          <button
            onClick={handlePayNow}
            disabled={step === 'processing'}
            className="w-full py-3 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {step === 'processing' ? 'Processing...' : `Pay ${formatMoney(total)}`}
          </button>

          <button
            onClick={() => { setStep('form'); setError('') }}
            disabled={step === 'processing'}
            className="w-full mt-3 py-2 text-text-muted hover:text-text text-sm transition-colors disabled:opacity-50"
          >
            Back to form
          </button>
        </div>
      </>
    )
  }

  // -- Form (initial step) --
  return (
    <>
      <Helmet>
        <title>Join the AU - ARPU</title>
        <meta name="description" content="Join the American Racing Pigeon Union online." />
      </Helmet>
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-heading font-bold text-text mb-2">Join the AU</h1>
        <p className="text-text-light mb-6">
          Become a member of the American Racing Pigeon Union. Fill out the form below to get started.
        </p>

        {error && (
          <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 text-sm mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmitForm}>
          {/* Membership type */}
          <div className="mb-6">
            <h2 className="text-lg font-heading font-semibold text-text mb-3">Membership Type</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {MEMBERSHIP_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`
                    block p-4 rounded-lg border cursor-pointer transition-all text-center
                    ${form.membershipType === opt.value
                      ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                      : 'border-border bg-surface hover:border-primary/30'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="membershipType"
                    value={opt.value}
                    checked={form.membershipType === opt.value}
                    onChange={() => updateField('membershipType', opt.value)}
                    className="sr-only"
                  />
                  <span className="block font-heading font-semibold text-text text-lg">
                    {formatMoney(opt.cost)}
                  </span>
                  <span className="block font-medium text-text mt-1">{opt.label}</span>
                  <span className="block text-xs text-text-muted mt-1">{opt.description}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Personal info */}
          <div className="mb-6">
            <h2 className="text-lg font-heading font-semibold text-text mb-3">Personal Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormInput label="First Name" value={form.firstName} error={fieldErrors.firstName} onChange={(v) => updateField('firstName', v)} />
              <FormInput label="Last Name" value={form.lastName} error={fieldErrors.lastName} onChange={(v) => updateField('lastName', v)} />
              <FormInput label="Address" value={form.address} error={fieldErrors.address} onChange={(v) => updateField('address', v)} className="sm:col-span-2" />
              <FormInput label="City" value={form.city} error={fieldErrors.city} onChange={(v) => updateField('city', v)} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-text mb-1">State</label>
                  <select
                    value={form.state}
                    onChange={(e) => updateField('state', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm ${
                      fieldErrors.state ? 'border-error' : 'border-border'
                    }`}
                  >
                    <option value="">Select</option>
                    {US_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  {fieldErrors.state && <p className="text-error text-xs mt-1">{fieldErrors.state}</p>}
                </div>
                <FormInput label="ZIP" value={form.zip} error={fieldErrors.zip} onChange={(v) => updateField('zip', v)} />
              </div>
              <FormInput label="Phone" value={form.phone} error={fieldErrors.phone} onChange={(v) => updateField('phone', formatPhoneInput(v))} type="tel" />
              {form.membershipType !== 'junior' && (
                <FormInput label="Email" value={form.email} error={fieldErrors.email} onChange={(v) => updateField('email', v)} type="email" />
              )}
            </div>
          </div>

          {/* Parent/guardian info (junior only) — no email is collected for the
              junior member; the guardian's email is the account contact. */}
          {form.membershipType === 'junior' && (
            <div className="mb-6">
              <h2 className="text-lg font-heading font-semibold text-text mb-1">Parent/Guardian Information</h2>
              <p className="text-sm text-text-muted mb-3">
                A parent or legal guardian must complete this enrollment. Junior members
                sign in with a username (assigned at the end of signup) instead of an
                email address, and all account emails go to the parent/guardian.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormInput label="Parent/Guardian Name" value={form.guardianName} error={fieldErrors.guardianName} onChange={(v) => updateField('guardianName', v)} />
                <FormInput label="Parent/Guardian Email" value={form.guardianEmail} error={fieldErrors.guardianEmail} onChange={(v) => updateField('guardianEmail', v)} type="email" />
              </div>
              <label className="flex items-start gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.guardianConsent}
                  onChange={(e) => updateField('guardianConsent', e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-text-light">
                  I am the parent or legal guardian of this junior member, and I consent
                  to the American Racing Pigeon Union collecting the information above to
                  create and manage their membership account. Account communications and
                  password resets will be sent to my email address.
                </span>
              </label>
              {fieldErrors.guardianConsent && (
                <p className="text-error text-xs mt-1">{fieldErrors.guardianConsent}</p>
              )}
            </div>
          )}

          {/* Account credentials */}
          <div className="mb-6">
            <h2 className="text-lg font-heading font-semibold text-text mb-3">Create Your Account</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormInput label="Password" value={form.password} error={fieldErrors.password} onChange={(v) => updateField('password', v)} type="password" />
              <FormInput label="Confirm Password" value={form.confirmPassword} error={fieldErrors.confirmPassword} onChange={(v) => updateField('confirmPassword', v)} type="password" />
            </div>
            <p className="text-xs text-text-muted mt-2">
              Minimum 8 characters.
              {form.membershipType === 'junior' && ' The parent/guardian sets and keeps this password.'}
            </p>
          </div>

          {/* Summary + submit */}
          <div className="bg-surface-alt border border-border rounded-lg p-4 mb-6">
            <div className="flex justify-between text-lg font-semibold text-text">
              <span>{selectedMembership.label} Membership</span>
              <span>{formatMoney(selectedMembership.cost)}</span>
            </div>
          </div>

          <button
            type="submit"
            disabled={step === 'processing'}
            className="w-full py-3 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {step === 'processing' ? 'Processing...' : `Continue to Payment - ${formatMoney(selectedMembership.cost)}`}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-text-muted space-y-2">
          <p>
            Already a member?{' '}
            <Link to="/login" className="text-primary hover:underline">Log in</Link>
            {' or '}
            <Link to="/claim" className="text-primary hover:underline">claim your account</Link>
          </p>
        </div>
      </div>
    </>
  )
}

function FormInput({ label, value, error, onChange, type = 'text', className = '' }: {
  label: string
  value: string
  error?: string
  onChange: (value: string) => void
  type?: string
  className?: string
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-text mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm ${
          error ? 'border-error' : 'border-border'
        }`}
      />
      {error && <p className="text-error text-xs mt-1">{error}</p>}
    </div>
  )
}
